<template>

    <div class="header">

        <div class="header__row" v-if="newslettersSearchingMode === ENUM.NEWSLETTERS.SEARCHING_MODE.UNACTIVE || isDesktop">
            <div class="header__progress" v-if="isDesktop">
                <div class="header__progress__left">
                    <circle-progress-bar
                            :percent="percent"></circle-progress-bar>
                </div>
                <div class="header__progress__right">
                    <p class="text-lMedium color__moon">
                        <span class="text-lSemibold color__charcoal">{{ this.calcDone }}</span>
                        <span>{{ `/${this.calcAll}` }}</span>
                    </p>
                    <p class="text-lRegular color__moon">
                        {{ $tc('cleaning_total_treated.treated_newsletter_formatted', this.calcDone) }}</p>
                </div>
            </div>

            <div class="header__mobile" v-show="!isDesktop">

                <div v-if="countSelectedNewsletters" class="header__mobile__selected"
                     @click="unselectAllNewsletters">

                    <img src="/img/newsletters-v2/close.svg" class="img__20 mr__4">
                    <p>{{ computedSelected }}</p>

                </div>

                <button class="header__btn__mail" v-else @click="openBSMS">
                    <p class="text-lSemibold header__btn__mail__text">{{ $t('cleaning_select_inbox') }}</p>
                    <img src="/img/newsletters-v2/chevron-down-blue.svg" class="img__24">
                </button>

            </div>

            <button class="header__btn__history" @click="goMyActions">
                <img src="/img/newsletters-v2/clock.svg" class="img__24 img__lang">
                <p class="text-lSemibold color__white header__btn__history__text">{{ $t('reverse_action') }}</p>
            </button>
        </div>

        <template v-if="newslettersSearchingMode === ENUM.NEWSLETTERS.SEARCHING_MODE.UNACTIVE">
            <div class="header__row">
                <div class="header__choice">
                    <button class="header__btn__template text-lSemibold"
                            :class="{'header__btn__template__active': newslettersPage === ENUM.NEWSLETTERS.PAGE.LIST}"
                            @click="clickUpdateNewslettersPage(ENUM.NEWSLETTERS.PAGE.LIST)">
                        {{ $t('cleaning_list') }}
                    </button>
                    <button class="header__btn__template text-lSemibold"
                            :class="{'header__btn__template__active': newslettersPage === ENUM.NEWSLETTERS.PAGE.CARDS}"
                            @click="clickUpdateNewslettersPage(ENUM.NEWSLETTERS.PAGE.CARDS)">
                        {{ $t('cleaning_cards') }}
                    </button>
                </div>

                <header-categories v-show="isDesktop"
                                   :category="category"
                                   :subscriptions="subscriptions"
                                   @updateCategory="updateCategory"></header-categories>

                <div class="header__line">
                    <button class="header__btn__search" @click="search(true)">
                        <img src="/img/newsletters-v2/search-blue.svg" class="img__24 img__lang">
                        <p class="text-lSemibold" v-show="isDesktop">{{ $t('cleaning_search_cta') }}</p>
                    </button>

                    <button class="header__btn__sort"
                            :class="{'header__btn__sort__active': newslettersSort !== ENUM.NEWSLETTERS.SORT.NOT_SET}"
                            @click="openSort">
                        <img src="/img/newsletters-v2/sort-ascending.svg" class="img__24">
                        <p class="text-lSemibold header__btn__sort__text">{{ computedSortTitle }}</p>
                    </button>
                </div>
            </div>

            <div class="divider"></div>

            <header-categories v-show="!isDesktop && isList"
                               :category="category"
                               :subscriptions="subscriptions"
                               @updateCategory="updateCategory"></header-categories>

        </template>

        <div class="header__search" v-show="newslettersSearchingMode !== ENUM.NEWSLETTERS.SEARCHING_MODE.UNACTIVE">

            <label class="header__search__label" for="searchInput">
                <img src="/img/newsletters-v2/search-grey.svg" class="img__24">
                <input type="text"
                       id="searchInput"
                       ref="searchInput"
                       v-model="modelSearchValue"
                       class="header__search__input text-lSemibold"
                       :placeholder="$t('cleaning_search_cta')">
                <img src="/img/newsletters-v2/x-blue.svg" class="img__24 ml__8 pointer"
                     @click="search(false)"
                     v-show="isDesktop">
            </label>
            <button class="text-lSemibold color__aquamarine ds__link__default"
                    @click="search(false)"
                    v-show="!isDesktop">
                {{ $t('cleaning_cancel_actions') }}
            </button>
        </div>

        <sort ref="SORT"></sort>

        <bottom-sheet-mailboxes-selection ref="BSMS"></bottom-sheet-mailboxes-selection>

    </div>

</template>

<script>
import Vuex from 'vuex'

import ENUM from '@/assets/ENUM'

import Storage from '@/helpers/storage'
import Tag from '@/modules/tag'

import BottomSheetMailboxesSelection from '@/components/bottomSheet/BottomSheetMailboxesSelection'
import CircleProgressBar from '@/components/newsletters-v2/CircleProgressBar'
import HeaderCategories from '@/components/newsletters-v2/HeaderCategories'
import Sort from '@/components/newsletters-v2/sort/Sort'

export default {

    name: 'newsletters-header',

    components: {
        BottomSheetMailboxesSelection,
        CircleProgressBar,
        HeaderCategories,
        Sort
    },

    props: {

        category: {
            type: String,
            default: ''
        },

        subscriptions: {
            type: Array,
            default: () => []
        },

        maxSubscriptions: {
            type: Number,
            default: 0
        }
    },

    watch: {

        modelSearchValue (newValue) {
            if(newValue !== this.searchValue) {
                this.updateSearchValue(newValue)
            }
        },

        searchValue (newValue) {
            if(newValue !== this.modelSearchValue) {
                this.modelSearchValue = newValue
            }
        }
    },

    computed: {

        ...Vuex.mapGetters([
            'countSelectedNewsletters',
            'newslettersPage',
            'newslettersSearchingMode',
            'newslettersSort',
            'searchValue'
        ]),

        computedSelected() {
            return this.$tc('cleaning_list_selected', this.countSelectedNewsletters, {nbSelected: this.countSelectedNewsletters})
        },

        ENUM() {
            return ENUM
        },

        calcAll() {
            return this.maxSubscriptions || 0
        },

        calcDone() {
            const treated = this.calcAll - this.subscriptions.length || 0
            return treated >= 0 ? treated : 0
        },

        progress() {
            return `${this.calcDone}/${this.calcAll}`
        },

        percent() {
            return (100 * this.calcDone) / this.calcAll
        },

        computedSortTitle() {
            if (this.isDesktop) {
                switch (this.newslettersSort) {
                    case ENUM.NEWSLETTERS.SORT.MOST_OPEN:
                        return this.$t('sort_by_most_open')
                    case ENUM.NEWSLETTERS.SORT.LEAST_OPEN:
                        return this.$t('sort_by_less_open')
                    case ENUM.NEWSLETTERS.SORT.ASCENDING:
                        return this.$t('sort_by_ascending')
                    case ENUM.NEWSLETTERS.SORT.DESCENDING:
                        return this.$t('sort_by_descending')
                    default:
                        return this.$t('sort_emails')
                }
            } else {
                return this.$t('sort_emails')
            }

        },

        isList() {
            return this.newslettersPage === ENUM.NEWSLETTERS.PAGE.LIST && !this.showEmpty
        }
    },

    data() {
        return {
            modelSearchValue: ''
        }
    },

    methods: {

        ...Vuex.mapActions([
            'unselectAllNewsletters',
            'updateNewslettersPage',
            'updateNewslettersSearchingMode',
            'updateSearchValue'
        ]),

        clickUpdateNewslettersPage(newPage) {
            newPage === ENUM.NEWSLETTERS.PAGE.LIST
                ? Tag.clickGA(this, 'list')
                : Tag.clickGA(this, 'cards')

            this.updateNewslettersPage(newPage)
        },

        updateCategory(newCategory) {
            this.$emit('updateCategory', newCategory)
        },

        openSort() {
            Tag.clickGA(this, 'sort_emails')
            this.$refs.SORT.open()
        },

        goMyActions() {
            Storage.setItem(Storage.KEYS.NEWSLETTERS_PAGE, this.page)
            this.$router.push({path: '/my-actions'})
        },

        search(bool) {
            this.isDesktop
                ? Tag.clickGA(this, 'search_cta')
                : Tag.clickGA(this, 'search_email')
            this.unselectAllNewsletters()
            if(bool) {
                this.updateNewslettersSearchingMode(ENUM.NEWSLETTERS.SEARCHING_MODE.SEARCHING)
            } else {
                this.updateSearchValue('')
                this.updateNewslettersSearchingMode(ENUM.NEWSLETTERS.SEARCHING_MODE.UNACTIVE)
                this.$forceUpdate()
            }
        },

        openBSMS() {
            this.$refs.BSMS.open()
        }
    }
}
</script>

<style lang="scss" scoped>

.header {

    &__row {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        padding: 8px 16px;
        background-color: $timberwolf;

        @media screen and (min-width: $above-tablet-length) {
            background-color: $white;
            border-bottom: 1px solid $divider;
        }
    }

    &__mobile {
        &__selected {
            display: flex;
            flex-direction: row;
            align-items: center;
            padding: 8px 16px 8px 12px;
            border-radius: 72px;
        }
    }

    &__progress {
        display: flex;
        flex-direction: row;

        &__right {
            text-align: start;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
        }
    }

    &__choice {
        border-radius: 72px;
        padding: 2px;
        background-color: $timberwolf;
        display: flex;
        flex-direction: row;
    }

    &__line {
        display: flex;
        flex-direction: row;
    }


    &__btn {
        &__mail {
            display: flex;
            flex-direction: row;
            padding: 8px 12px 8px 16px;
            align-items: center;
            border-radius: 72px;
            background-color: $white;
            color: $charcoal;

            &__text {
                margin-right: 8px;

                &:lang(ar-sa) {
                    margin-left: 8px;
                    margin-right: 0;
                }
            }

        }

        &__history {
            display: flex;
            flex-direction: row;
            padding: 8px 16px;
            align-items: center;
            background-color: $aquamarine;
            border-radius: 72px;

            &__text {
                margin-left: 8px;

                &:lang(ar-sa) {
                    margin-right: 8px;
                    margin-left: 0;
                }
            }
        }

        &__template {
            padding: 8px 16px;
            border-radius: 72px;
            color: $midnight;
            background-color: $timberwolf;

            &__active {
                background-color: $midnight;
                color: $white;
            }
        }

        &__search {
            display: flex;
            background-color: $white;
            align-items: center;
            justify-content: center;
            border-radius: 72px;
            padding: 12px;
            margin-right: 8px;
            @media screen and (min-width: $above-tablet-length) {
                margin-right: 20px;
            }

            &:lang(ar-sa) {
                margin-left: 20px;
                margin-right: 0;
            }

            > p {
                margin-left: 12px;

                &:lang(ar-sa) {
                    margin-left: 0;
                    margin-right: 12px;
                }
            }
        }

        &__sort {
            display: flex;
            flex-direction: row;
            align-items: center;
            background-color: $white;
            border-radius: 72px;
            padding: 8px 16px;
            @media screen and (min-width: $above-tablet-length) {
                padding: 8px 12px;
            }

            &__active {
                border: solid 2px $aquamarine;
                color: $aquamarine;
            }

            &__text {
                margin-left: 8px;

                &:lang(ar-sa) {
                    margin-left: 0;
                    margin-right: 8px;
                }
            }
        }
    }

    &__search {
        display: flex;
        flex-direction: row;
        padding: 16px;
        background-color: $timberwolf;

        @media screen and (min-width: $above-tablet-length) {
            background-color: $white;
            justify-content: center;
            border-bottom: 1px solid $divider;
        }

        &__label {
            padding: 8px 16px;
            display: flex;
            flex: 1;
            flex-direction: row;
            align-items: center;
            border-radius: 72px;
            background-color: $white;
            max-width: 650px;
            margin-right: 16px;

            &:lang(ar-sa) {
                margin-left: 16px;
                margin-right: 0;
            }
            @media screen and (min-width: $above-tablet-length) {
                border: 2px solid $silver;
            }

            >img {
                margin-right: 8px;
                &:lang(ar-sa) {
                    margin-left: 8px;
                    margin-right: 0;
                }
            }
        }

        &__input {
            width: 100%;
        }

        &__input::placeholder {
            font-size: 17px;
            font-weight: 600;
            line-height: 1.41;
            color: $stone;
        }
    }
}

.divider {
    height: 1px;
    width: 100%;
    background-color: $silver;
}
</style>
